/* eslint max-len: 0, array-bracket-spacing: 0 */
import { select } from "d3-selection"
import { line } from "d3-shape"
import { transition } from "d3-transition"
import debounce from "lodash/debounce"

import generateSeating from "./seating"

import { enhanceWithSeats } from "utils/data/parliament"

const parliamentchart = () => {
  const config = {
    opacityNonHighlighted: 0.2,
  }
  let displayedData
  let seating
  let model
  let element
  let svg
  let canvasIEFix
  let holder
  let labelsHolder
  let name
  let path
  let vbRatio
  const pathData = []

  const getLabelSize = () =>
    config.width > 960
      ? 48
      : config.width > 768
      ? 42
      : config.width > 640
      ? 36
      : config.width > 480
      ? 30
      : 22

  const getIconBackground = d => {
    const img = `${d.politicalParty.replace("og:", "")}.svg`
    const url = `/static/images/parties/svg/${img}`
    return `rgba(255,255,255,0.8) url(${url}) 0 50% / ${
      getLabelSize() - 4
    }px no-repeat`
  }

  const getOpacity = d =>
    !model.highlighted ||
    !model.highlighted.length ||
    model.highlighted.indexOf(d.politicalParty) !== -1
      ? 1
      : config.opacityNonHighlighted

  const l = line()
    .x(d => d.x)
    .y(d => d.y)

  const translateAlong = (path, labelSize, reverse) => {
    const l = path.getTotalLength()
    const [leftOffset, topOffset] = seating.viewBox

    return t => {
      const p = path.getPointAtLength(reverse ? (1 - t) * l : t * l)
      p.x = (-leftOffset + p.x) * vbRatio - labelSize / 2
      p.y = (-topOffset + p.y) * vbRatio - labelSize / 2
      return `translate(${p.x}px,${p.y}px)`
    }
  }

  const drawSeats = data => {
    const t = transition().duration(200)

    const circles = holder.selectAll("circle").data(data)

    circles
      .enter()
      .append("circle")
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", d => d.r)
      .attr("fill", d => d.fill)
      .attr("fill-opacity", 0)
      .merge(circles)
      .transition(t)
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", d => d.r)
      .attr("fill", d => d.fill)
      .attr("fill-opacity", getOpacity)

    circles.exit().transition(t).attr("fill-opacity", 0).remove()
  }

  const drawLabels = data => {
    const t = transition().duration(1000)
    const [leftOffset, topOffset] = seating.viewBox
    const labelSize = getLabelSize()

    const labelWrappers = labelsHolder
      .selectAll("div.label-wrapper")
      .data(data, d => d.politicalParty)

    const labelWrappersEnter = labelWrappers
      .enter()
      .append("div")
      .attr("class", "label-wrapper")
      .attr("id", d => `label-wrapper-${d.politicalParty}`)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("left", 0)
      .style("top", 0)
      .style("width", () => `${labelSize}px`)
      .style("height", () => `${labelSize}px`)
      .style("transform", d => {
        const x = (-leftOffset + d.x) * vbRatio - labelSize / 2
        const y = (-topOffset + d.y) * vbRatio - labelSize / 2
        return `translate(${x}px, ${y}px)`
      })
      .attr("data-ox", d => d.x)
      .attr("data-oy", d => d.y)
      .merge(labelWrappers)
      .transition(t)
      .style("opacity", getOpacity)
      .style("width", () => `${labelSize}px`)
      .style("height", () => `${labelSize}px`)
      .styleTween("transform", function (to) {
        const from = {
          x: +this.getAttribute("data-ox"),
          y: +this.getAttribute("data-oy"),
        }
        const reversed = from.x > to.x
        const sliced = [
          reversed ? to : from,
          ...pathData.filter(p =>
            reversed
              ? to.x <= p.x && from.x >= p.x
              : from.x <= p.x && to.x >= p.x
          ),
          reversed ? from : to,
        ]

        const p = svg
          .selectAll(`#path-${to.politicalParty.replace(":", "_")}`)
          .data([{ ...to, data: sliced }], d =>
            d.politicalParty.replace(":", "_")
          )

        p.enter()
          .append("path")
          .attr("id", d => `path-${d.politicalParty.replace(":", "_")}`)
          .attr("fill", "none")
          .attr("d", d => l(d.data))
          .merge(p)
          .attr("d", d => l(d.data))

        p.exit().remove()

        return p.node()
          ? translateAlong(p.node(), labelSize, reversed)
          : undefined
      })
      .attr("data-ox", d => d.x)
      .attr("data-oy", d => d.y)

    labelWrappers.exit().transition(t).style("opacity", 0).remove()

    const labels = labelWrappers
      .merge(labelWrappersEnter)
      .selectAll("div.label")
      .data(
        d => [d],
        d => d.politicalParty
      )

    labels
      .enter()
      .append("div")
      .attr("class", "label")
      .style("width", () => `${labelSize}px`)
      .style("height", () => `${labelSize}px`)
      .style("border-radius", "50%")
      .style("border", d => `2px solid ${d.fill}`)
      .style("background", getIconBackground)
      .merge(labels)
      .style("width", () => `${labelSize}px`)
      .style("height", () => `${labelSize}px`)
      .style("border", d => `2px solid ${d.fill}`)
      .style("background", getIconBackground)

    labels.exit().remove()

    const counts = labelWrappers
      .merge(labelWrappersEnter)
      .selectAll("div.label-count")
      .data(config.width > 380 ? d => [d] : [], d => d.politicalParty)

    counts
      .enter()
      .append("div")
      .attr("class", "label-count")
      .style("position", "absolute")
      .style("border-radius", "50%")
      .style("color", "#fff")
      .style("text-align", "center")
      .style("top", () => `${-labelSize * 0.5}px`)
      .style("left", d => `${d.x < 0 ? -labelSize * 0.25 : labelSize * 0.5}px`)
      .style("height", () => `${labelSize * 0.75}px`)
      .style("width", () => `${labelSize * 0.75}px`)
      .style("font-size", () => `${labelSize * 0.6}px`)
      .style("line-height", () => `${labelSize * 0.75}px`)
      .style("background-color", d => `${d.fill}`)
      .text(
        d =>
          displayedData.find(dd => d.politicalParty === dd.politicalParty).seats
      )
      .merge(counts)
      .style("top", () => `${-labelSize * 0.5}px`)
      .style("height", () => `${labelSize * 0.75}px`)
      .style("width", () => `${labelSize * 0.75}px`)
      .style("font-size", () => `${labelSize * 0.4}px`)
      .style("line-height", () => `${labelSize * 0.75}px`)
      .style("background-color", d => `${d.fill}`)
      .text(
        d =>
          displayedData.find(dd => d.politicalParty === dd.politicalParty).seats
      )
      .transition(t)
      .style("left", d => `${d.x < 0 ? -labelSize * 0.25 : labelSize * 0.5}px`)

    counts.exit().remove()
  }

  const drawSeatCount = data => {
    const { radius } = seating
    const c = svg.selectAll("text.count").data(data)

    c.enter()
      .append("text")
      .attr("class", "count")
      .attr("x", 0)
      .attr("y", 0)
      .attr("text-anchor", "middle")
      .style("font-size", `${0.15 * radius}px`)
      .style("font-weight", "900")
      .style("fill", "#222")
      .text(d => d)
      .merge(c)
      .style("fill", "#222")
      .text(d => d)

    c.exit().remove()
  }

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180.0
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad),
    }
  }

  const describeArc = (x, y, r, start, end) => {
    const s = polarToCartesian(x, y, r, end)
    const e = polarToCartesian(x, y, r, start)
    const arcFlag = end - start <= 180 ? "0" : "1"
    return ["M", s.x, s.y, "A", r, r, 0, arcFlag, 0, e.x, e.y].join(" ")
  }

  const update = m => {
    if (m) model = m
    if (!model.data) return
    if (model.config) Object.assign(config, model.config)

    displayedData = enhanceWithSeats(model.data[model.date]).sort(
      (a, b) => a.order - b.order
    )

    seating = generateSeating(displayedData)
    const [vbWidth, vbHeight] = seating.viewBox.slice(-2)

    config.width = element.parentNode.offsetWidth
    config.height = config.width / (vbWidth / vbHeight)
    vbRatio = config.width / vbWidth

    svg.attr("viewBox", seating.viewBox.join(","))
    canvasIEFix.attr("width", vbWidth).attr("height", vbHeight)

    holder.attr(
      "transform",
      model.withLabels
        ? `scale(${(config.width - getLabelSize() * 2.1) / config.width})`
        : null
    )
    drawSeats(seating.points)
    drawSeatCount(model.withSeatCount ? [seating.points.length] : [])
    drawLabels(model.withLabels ? seating.outsideRingCenters : [])
  }

  const create = (el, model = {}) => {
    name = `parliamenchart-${btoa(+new Date())}`
    element = el
    svg = select(element)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("width", "100%")
    holder = svg.append("g")
    labelsHolder = select(element.parentNode)
      .insert("div", ":first-child")
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
    // IE11 doesn't scale SVGs properly, but it does
    // scale canvas elements
    canvasIEFix = select(element.parentNode)
      .insert("canvas", ":first-child")
      .style("width", "100%")
      .style("visibility", "hidden")

    // path along which the labels will be transitioned
    path = svg
      .append("path")
      .attr("fill", "none")
      .attr("stroke-width", 0.05)
      .attr("d", describeArc(0, 0, 20, -90, 90))

    // collect path data points to create path segments for later transitions
    const l = path.node().getTotalLength()
    for (let i = 1; i >= 0; i -= 0.01) {
      pathData.push(path.node().getPointAtLength(l * i))
    }

    select(window).on(`resize.${name}`, debounce(update, 50))
    update(model)
  }

  const destroy = () => {
    while (element.firstChild) element.removeChild(element.firstChild)
    select(window).on(`resize.${name}`, null)
  }

  return { create, update, destroy }
}

export default parliamentchart
